import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  styled,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import libraries from './libraries.json';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useLookups } from '../../hooks/useLookups';
import {
  selectUserApiInfo,
  selectLoggedInUserInfo,
  fetchUserApiInfoAsync,
} from '../../data/user.slice';
import {
  selectLookupsApiInfo,
  selectApplicationSettingsUnfiltered,
  fetchLookupsApiInfoAsync,
} from '../../data/lookup.slice';
import {
  selectTransportApiInfo,
  fetchTransportApiInfoAsync,
} from '../../data/provider.slice';

interface AboutDialogProps {
  onClose: () => void;
  open: boolean;
}

type UserPermissionType = {
  resource: string;
  action: string;
  scope: string;
};

type FeatureFlagInfo = {
  applicationSettingBC: string;
  applicationSettingName: string;
  description: string;
};

export default function AboutDialog(props: AboutDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const { __environment__ } = window as any;

  // This dialog is the only consumer of the three API-info endpoints and of
  // `applicationSetting`. All four used to be fetched on boot for every user;
  // they are only needed once someone opens About, which is rare.
  useLookups(props.open ? ['applicationSetting'] : []);
  useEffect(() => {
    if (!props.open) return;
    dispatch(fetchUserApiInfoAsync());
    dispatch(fetchLookupsApiInfoAsync());
    dispatch(fetchTransportApiInfoAsync());
  }, [props.open, dispatch]);

  const userApiInfo = useAppSelector(selectUserApiInfo);
  const lookupsApiInfo = useAppSelector(selectLookupsApiInfo);
  const transportApiInfo = useAppSelector(selectTransportApiInfo);
  const loggedInUserInfo = useAppSelector(selectLoggedInUserInfo);
  const applicationSettings = useAppSelector(
    selectApplicationSettingsUnfiltered,
  );
  const { onClose, open } = props;
  const [showAppVersionContent, setShowAppVersionContent] = useState(false);

  const appVersion = useMemo(() => {
    const version = __environment__.REACT_APP_VERSION;
    return version.split('.').slice(0, 2).join('.');
  }, [__environment__.REACT_APP_VERSION]);
  const envVarList = Object.keys(__environment__).filter(
    (envVar) =>
      envVar.startsWith('REACT_APP') &&
      envVar !== 'REACT_APP_VERSION' &&
      !envVar.includes('KEY'),
  );

  const featureFlags = useMemo<FeatureFlagInfo[]>(() => {
    const flags = loggedInUserInfo?.featureFlags || [];
    return flags.flatMap((flagBC: string) => {
      const setting = applicationSettings.find(
        (s) => s.applicationSettingBC === flagBC,
      );
      return setting
        ? [
            {
              applicationSettingBC: setting.applicationSettingBC,
              applicationSettingName: setting.applicationSettingName,
              description: setting.description || '',
            },
          ]
        : [];
    });
  }, [loggedInUserInfo?.featureFlags, applicationSettings]);

  const handleClose = () => {
    onClose();
    setShowAppVersionContent(false);
  };

  const [showPermissions, setShowPermissions] = useState(true);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.ctrlKey && event.key === 'i') {
      // Handle the "ctrl + i" keystroke
      setShowPermissions((oldVlaue) => !oldVlaue);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <>
      <Dialog disableEscapeKeyDown fullWidth open={open} onClose={onClose}>
        <DialogTitle>{t('about.title')}</DialogTitle>
        {!showAppVersionContent ? (
          <DialogContent>
            <p onClick={() => setShowAppVersionContent(true)}>
              <strong>{t('about.appVersion')}:</strong> {appVersion}
            </p>
          </DialogContent>
        ) : (
          <DialogContent>
            <p>{loggedInUserInfo?.emailAddress}</p>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {t('about.appVersion')}: {__environment__.REACT_APP_VERSION}
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  <ListItemText
                    primary={
                      t('about.userApiVersion') +
                      `: ${userApiInfo?.version || ''}`
                    }
                  />
                  <ListItemText
                    primary={
                      t('about.lookupsApiVersion') +
                      `: ${lookupsApiInfo?.version || ''}`
                    }
                  />
                  <ListItemText
                    primary={
                      t('about.transportApiVersion') +
                      `: ${transportApiInfo?.version || ''}`
                    }
                  />
                </List>
              </AccordionDetails>
            </Accordion>
            {showPermissions && (
              <>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    {t('about.userGroups')}
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {loggedInUserInfo?.access?.groups?.map(
                        (group: string, index: number) => (
                          <ListItem key={index}>
                            <ListItemText primary={group} />
                          </ListItem>
                        ),
                      )}
                    </List>
                  </AccordionDetails>
                </Accordion>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    {t('about.userPermissions')}
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {loggedInUserInfo?.access?.permissions?.map(
                        (permission: UserPermissionType, index: number) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={`${permission.resource}.${permission.action}.${permission.scope}`}
                            />
                          </ListItem>
                        ),
                      )}
                    </List>
                  </AccordionDetails>
                </Accordion>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    {t('about.userRoles')}
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {loggedInUserInfo?.access?.userRoles?.map(
                        (role: string, index: number) => (
                          <ListItem key={index}>
                            <ListItemText primary={role} />
                          </ListItem>
                        ),
                      )}
                    </List>
                  </AccordionDetails>
                </Accordion>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    {t('about.featureFlags')}
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {featureFlags.map(
                        (flag: FeatureFlagInfo, index: number) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={`${flag.applicationSettingBC} - ${flag.applicationSettingName}`}
                              secondary={flag.description}
                            />
                          </ListItem>
                        ),
                      )}
                    </List>
                  </AccordionDetails>
                </Accordion>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    {t('about.environmentVariables')}
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {envVarList.map((envVar) => (
                        <ListItem key={envVar}>
                          <ListItemText
                            primary={envVar}
                            secondary={`${__environment__[envVar]}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </>
            )}

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {t('about.openLibrariesUsed')}
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {libraries.map(({ name, githubLink }) => (
                    <ListItemButton key={name} href={githubLink}>
                      <ListItemText primary={name} />
                    </ListItemButton>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          </DialogContent>
        )}

        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleClose()}>
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
